import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, ChefHat, Clock, Home as HomeIcon, LogOut, RefreshCw, Undo2, X } from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { InstallAppBanner } from "@/hooks/usePWAManifest";

const TOKEN_KEY = "bk_admin_token";
const ACTIVE = ["Received", "Preparing", "Ready"];

const NEXT_LABEL = {
  Received: "Start Cooking",
  Preparing: "Mark Ready",
  Ready: "Picked Up",
};
const NEXT_STATUS = {
  Received: "Preparing",
  Preparing: "Ready",
  Ready: "Completed",
};
const BTN_TINT = {
  Received: "bg-amber-500 hover:bg-amber-600",
  Preparing: "bg-emerald-600 hover:bg-emerald-700",
  Ready: "bg-stone-700 hover:bg-stone-800",
};

/** Live ticket age in minutes. Re-rendered each tick. */
function elapsedMin(iso) {
  return Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
}

function elapsed(iso) {
  const m = Math.floor(elapsedMin(iso));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

/**
 * Ticket urgency ring — the whole card border/ring changes color as the
 * ticket ages so the kitchen instantly sees which tickets are slipping.
 *   0 – 4:59 min = green (fresh)
 *   5 – 9:59 min = yellow (getting old)
 *   10+ min      = red (urgent)
 */
function urgencyClasses(iso) {
  const m = elapsedMin(iso);
  if (m >= 10) return { border: "border-red-500", ring: "ring-red-400", pill: "bg-red-600 text-white", label: "URGENT" };
  if (m >= 5) return { border: "border-yellow-500", ring: "ring-yellow-400", pill: "bg-yellow-500 text-stone-900", label: "OLD" };
  return { border: "border-emerald-500", ring: "ring-emerald-300", pill: "bg-emerald-600 text-white", label: "FRESH" };
}

export default function KitchenPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0); // re-render for elapsed time
  const [soundOn, setSoundOn] = useState(
    () => localStorage.getItem("bk_kitchen_sound") !== "off"
  );
  const [flashSet, setFlashSet] = useState(new Set()); // order_numbers freshly arrived
  const [showRecall, setShowRecall] = useState(false);
  const [recallList, setRecallList] = useState([]);
  const { settings } = useSiteSettings();
  const seenRef = useRef(null); // Set of order_numbers ever seen (null = not initialized)
  const audioCtxRef = useRef(null);

  /**
   * Play a chime for a new order. Online orders get a louder, longer,
   * repeating 3-beep alert so the cook glances up without the cashier
   * having to call out. POS orders get the softer 2-tone ding-dong since
   * the cashier who just entered it doesn't need to be re-alerted.
   */
  const playChime = useCallback((variant = "pos") => {
    if (!soundOn) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const notes = variant === "online"
        ? [
            // 3-beep urgent pattern @ 1000Hz
            { f: 1000, t: 0.0, gain: 0.6 },
            { f: 1000, t: 0.30, gain: 0.6 },
            { f: 1000, t: 0.60, gain: 0.6 },
            { f: 1320, t: 0.90, gain: 0.55 },
          ]
        : [
            // Soft 2-tone ding-dong
            { f: 880, t: 0.0, gain: 0.35 },
            { f: 660, t: 0.22, gain: 0.35 },
          ];
      notes.forEach(({ f, t, gain }) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        g.gain.setValueAtTime(0, now + t);
        g.gain.linearRampToValueAtTime(gain, now + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, now + t + 0.28);
        osc.connect(g).connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.32);
      });
    } catch (err) {
      console.debug("Kitchen chime unavailable:", err?.message || err);
    }
  }, [soundOn]);

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      localStorage.setItem("bk_kitchen_sound", next ? "on" : "off");
      if (next) playChime("online"); // preview the louder variant
      return next;
    });
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      toast.error("Invalid passcode");
    }
  };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await adminApi(token).get("/orders");
      // Detect new "Received" orders vs. the last known set
      const active = data.filter((o) => ACTIVE.includes(o.status));
      const currentNums = active.map((o) => o.order_number);
      if (seenRef.current === null) {
        // First load — seed without firing sound
        seenRef.current = new Set(currentNums);
      } else {
        const fresh = active.filter(
          (o) => o.status === "Received" && !seenRef.current.has(o.order_number)
        );
        if (fresh.length > 0) {
          // Online orders get the LOUD 3-beep alert so the cook glances up
          // without the cashier calling out. POS orders (which the cashier
          // just entered on the register) get the softer ding-dong.
          const anyOnline = fresh.some((o) => o.channel !== "pos");
          playChime(anyOnline ? "online" : "pos");
          setFlashSet((prev) => {
            const next = new Set(prev);
            fresh.forEach((o) => next.add(o.order_number));
            return next;
          });
          // remove flash after 8s
          fresh.forEach((o) => {
            setTimeout(() => {
              setFlashSet((prev) => {
                const next = new Set(prev);
                next.delete(o.order_number);
                return next;
              });
            }, 8000);
          });
        }
        currentNums.forEach((n) => seenRef.current.add(n));
      }
      setOrders(data);
    } catch (err) {
      // token may be stale, network may be down; log but don't spam the UI
      console.debug("Kitchen orders fetch failed:", err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [token, playChime]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    const t2 = setInterval(() => setTick((x) => x + 1), 10000);
    return () => {
      clearInterval(t);
      clearInterval(t2);
    };
  }, [load]);

  const active = useMemo(
    () =>
      orders
        .filter((o) => ACTIVE.includes(o.status))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [orders]
  );

  const advance = async (o) => {
    try {
      await adminApi(token).put(`/orders/${o.id}/status`, {
        status: NEXT_STATUS[o.status],
      });
      toast.success(`${o.order_number} → ${NEXT_STATUS[o.status]}`);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center p-6">
        <InstallAppBanner
          manifest="/manifest-kds.json"
          themeColor="#1c1917"
          appleTitle="BK Kitchen"
          label="BK Kitchen (KDS)"
        />
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-brand-red flex items-center justify-center mb-3">
              <ChefHat className="w-7 h-7 text-brand-yellow" />
            </div>
            <div className="font-display text-2xl text-brand-red">Kitchen Display</div>
            <div className="text-xs text-stone-500 uppercase tracking-widest">
              Blossom&apos;s Kitchen
            </div>
          </div>
          <form onSubmit={login} className="space-y-3">
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              required
              data-testid="kitchen-login-input"
              className="w-full px-3 py-3 rounded-lg border border-stone-300 outline-none focus:border-brand-red text-base"
            />
            <button
              type="submit"
              data-testid="kitchen-login-submit"
              className="w-full bg-brand-red text-white py-3 rounded-full font-bold uppercase text-sm tracking-widest hover:bg-red-800"
            >
              Open Kitchen
            </button>
          </form>
        </div>
      </div>
    );
  }

  const stats = {
    received: orders.filter((o) => o.status === "Received").length,
    preparing: orders.filter((o) => o.status === "Preparing").length,
    ready: orders.filter((o) => o.status === "Ready").length,
  };

  return (
    <div className="min-h-screen bg-stone-900 text-white">
      <InstallAppBanner
        manifest="/manifest-kds.json"
        themeColor="#1c1917"
        appleTitle="BK Kitchen"
        label="BK Kitchen (KDS)"
      />
      <div className="chalkboard-band border-b-4 border-brand-red sticky top-0 z-10">
        <div className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-brand-yellow" />
            </div>
            <div>
              <div className="font-display text-brand-yellow text-xl leading-none">
                Kitchen Display
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">
                Blossom&apos;s Kitchen
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <StatChip label="New" n={stats.received} color="text-blue-300" />
            <StatChip label="Cooking" n={stats.preparing} color="text-amber-300" />
            <StatChip label="Ready" n={stats.ready} color="text-emerald-300" />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="kitchen-refresh-btn"
              className="text-white/70 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-2 py-2"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link
              to="/admin"
              className="text-white/70 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-2 py-2"
              data-testid="kitchen-admin-link"
            >
              Admin
            </Link>
            <Link
              to="/"
              className="text-white/70 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-2 py-2"
            >
              <HomeIcon className="w-4 h-4" />
            </Link>
            <button
              onClick={() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
              }}
              className="text-white/70 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-2 py-2"
              data-testid="kitchen-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="text-center font-hand text-4xl text-stone-500 py-20">
            Loading queue...
          </div>
        ) : active.length === 0 ? (
          <div className="text-center py-24">
            <div className="font-hand text-5xl text-stone-500 mb-2">All caught up!</div>
            <div className="text-sm text-stone-500 uppercase tracking-widest">
              No active orders
            </div>
          </div>
        ) : (
          <div
            data-testid="kitchen-board"
            className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {active.map((o) => {
              const u = urgencyClasses(o.created_at);
              const isOnline = o.channel !== "pos";
              const cardStyle = isOnline
                ? {
                    background: settings.kds_online_bg || "#FDD835",
                    color: settings.kds_online_text || "#000",
                  }
                : {
                    background: settings.kds_pos_bg || "#ffffff",
                    color: settings.kds_pos_text || "#111",
                  };
              return (
              <div
                key={o.id}
                data-testid={`kitchen-card-${o.order_number}`}
                style={cardStyle}
                className={`border-l-8 ${u.border} rounded-xl p-4 flex flex-col gap-3 shadow-lg ${
                  flashSet.has(o.order_number) ? "ring-4 ring-brand-red animate-new-order" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-brand-red text-2xl leading-none">
                      {o.order_number}
                    </div>
                    <div className="text-xs uppercase tracking-widest opacity-80 font-bold mt-1">
                      {o.customer_name}
                    </div>
                    <div
                      className={`inline-block mt-2 text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded ${
                        o.channel === "pos"
                          ? "bg-brand-red text-white"
                          : "bg-stone-900 text-brand-yellow"
                      }`}
                      data-testid={`kitchen-source-${o.order_number}`}
                    >
                      {o.channel === "pos"
                        ? (o.beeper_number
                            ? `WINDOW · Beeper #${o.beeper_number}`
                            : "WINDOW")
                        : "ONLINE PICKUP"}
                    </div>
                    {o.curbside && (
                      <div className={`mt-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded ${o.curbside.delivered_at ? "bg-emerald-100 text-emerald-800" : o.curbside.checked_in_at ? "bg-brand-yellow text-stone-900 animate-pulse" : "bg-stone-200 text-stone-700"}`} data-testid={`kitchen-curbside-${o.order_number}`}>
                        🚗 {o.curbside.delivered_at ? "Brought out" : o.curbside.checked_in_at ? "Curbside — here now" : "Curbside — not arrived"}{o.curbside.vehicle ? ` · ${o.curbside.vehicle}` : ""}{o.curbside.spot ? ` · ${o.curbside.spot}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div
                      className={`inline-block text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded ${u.pill}`}
                      data-testid={`kitchen-urgency-${o.order_number}`}
                    >
                      {u.label}
                    </div>
                    <div
                      key={tick}
                      data-testid={`kitchen-timer-${o.order_number}`}
                      className={`text-xs font-mono font-black mt-1 flex items-center gap-1 justify-end ${
                        elapsedMin(o.created_at) >= 10
                          ? "text-red-600"
                          : elapsedMin(o.created_at) >= 5
                          ? "text-yellow-700"
                          : "text-emerald-700"
                      }`}
                    >
                      <Clock className="w-3 h-3" />
                      {elapsed(o.created_at)}
                    </div>
                  </div>
                </div>

                <ul className="text-base space-y-1 border-t border-stone-300/60 pt-2">
                  {o.items.map((it, idx) => (
                    <li
                      key={`${it.item_id}-${idx}`}
                      className="flex items-baseline justify-between gap-2"
                    >
                      <div>
                        <span className="font-black text-brand-red text-lg mr-1.5">
                          {it.quantity}×
                        </span>
                        <span className="font-bold text-stone-800">{it.name}</span>
                        {it.modifications && (
                          <div className="text-xs italic text-brand-red font-bold ml-6">
                            ⚑ {it.modifications}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                {o.notes && (
                  <div
                    className={`text-sm rounded p-2 ${
                      /^ALLERGY:/i.test(o.notes)
                        ? "bg-red-600 border-2 border-red-800 text-white animate-pulse"
                        : "bg-brand-yellow/40 border-2 border-brand-yellow text-stone-900"
                    }`}
                    data-testid={`kds-note-${o.id}`}
                  >
                    <span className="font-black uppercase tracking-widest text-[10px] mr-1">
                      {/^ALLERGY:/i.test(o.notes) ? "⚠ Allergy:" : "Note:"}
                    </span>
                    {o.notes.replace(/^ALLERGY:\s*/i, "")}
                  </div>
                )}

                <div className="text-[11px] uppercase tracking-widest text-stone-600 font-bold">
                  Pickup: <span className="text-stone-900">{o.pickup_time}</span>
                </div>

                <button
                  onClick={() => advance(o)}
                  data-testid={`kitchen-bump-btn-${o.order_number}`}
                  className={`w-full text-white font-black uppercase tracking-widest text-sm py-4 rounded-lg active:scale-[0.98] transition-transform ${
                    BTN_TINT[o.status]
                  }`}
                >
                  {NEXT_LABEL[o.status]}
                </button>
              </div>
              );
            })}
          </div>
        )}
        <RecallButton
          token={token}
          show={showRecall}
          setShow={setShowRecall}
          recallList={recallList}
          setRecallList={setRecallList}
          onRecalled={load}
        />
      </div>
    </div>
  );
}

/**
 * RecallButton — floating "Recall bumped order" pill in the bottom right.
 * Opens a drawer of orders bumped in the last 30 minutes. Tapping one
 * fires POST /orders/{id}/recall which sets status back to Received so
 * the ticket re-appears at the top of the KDS.
 */
function RecallButton({ token, show, setShow, recallList, setRecallList, onRecalled }) {
  const loadRecall = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/orders/recall-candidates?minutes=30");
      setRecallList(data);
    } catch {
      toast.error("Couldn't load recall list");
    }
  }, [token, setRecallList]);

  const open = async () => { await loadRecall(); setShow(true); };
  const recall = async (o) => {
    try {
      await adminApi(token).post(`/orders/${o.id}/recall`);
      toast.success(`Recalled #${o.order_number}`);
      setShow(false);
      onRecalled();
    } catch {
      toast.error("Recall failed");
    }
  };

  return (
    <>
      <button
        onClick={open}
        data-testid="kitchen-recall-btn"
        className="fixed bottom-4 right-4 z-40 bg-stone-800 hover:bg-stone-900 text-brand-yellow px-4 py-3 rounded-full shadow-2xl flex items-center gap-2 text-xs font-black uppercase tracking-widest"
      >
        <Undo2 className="w-4 h-4" />
        Recall Order
      </button>
      {show && (
        <div
          onClick={() => setShow(false)}
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          data-testid="kitchen-recall-drawer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-brand-red text-2xl">Recall a bumped order</div>
              <button onClick={() => setShow(false)}><X className="w-5 h-5 text-stone-500" /></button>
            </div>
            <p className="text-xs text-stone-500">
              Tickets bumped in the last 30 minutes. Tap any to send it back to the KDS.
            </p>
            {recallList.length === 0 ? (
              <div className="text-center py-8 text-stone-400 text-sm">
                Nothing to recall — no orders bumped recently.
              </div>
            ) : (
              recallList.map((o) => (
                <button
                  key={o.id}
                  onClick={() => recall(o)}
                  data-testid={`kitchen-recall-item-${o.order_number}`}
                  className="w-full text-left border border-stone-200 hover:border-brand-red rounded-lg px-3 py-2 flex items-center justify-between hover:bg-brand-red/5"
                >
                  <div>
                    <div className="font-display text-brand-red text-xl">{o.order_number}</div>
                    <div className="text-[11px] text-stone-500">
                      {o.customer_name} · {o.channel === "pos" ? (o.beeper_number ? `Beeper #${o.beeper_number}` : "WINDOW") : "ONLINE"}
                    </div>
                  </div>
                  <Undo2 className="w-4 h-4 text-brand-red" />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}

function StatChip({ label, n, color }) {
  return (
    <div className="flex items-baseline gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
      <span className={`font-display text-xl ${color}`}>{n}</span>
      <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
        {label}
      </span>
    </div>
  );
}
